<template>
  <!-- Dashboard de rendimientos (project.configs.dashboards → suffix_route: 'performance') -->
  <div class="-mx-4 -mt-4">
    <page-header :title="$t('performance_dashboard_review_title')" :subtitle="headerSubtitle">
      <template #title-append>
        <div v-if="projectWeek" class="inline-flex items-center gap-2 px-3 py-1 bg-white border border-grey-light rounded">
          <b-icon pack="fas" icon="calendar" size="is-small" class="text-blue-dark" />
          <div class="leading-tight text-center">
            <div class="text-sm font-semibold text-blue-dark whitespace-nowrap">
              {{ $t('performance_dashboard_project_week', { week: projectWeek.weekNumber, total: projectWeeks.length, year: projectWeek.year }) }}
            </div>
            <div class="text-xs text-grey-dark whitespace-nowrap">{{ projectWeek.range }}</div>
          </div>
        </div>
      </template>
    </page-header>

    <div class="flex flex-col gap-3 p-4 text-xs text-grey-darkest">

      <!-- Fecha de corte -->
      <div class="px-4 py-2 bg-white border border-grey-light rounded-lg">
        <span class="font-bold">{{ $t('performance_dashboard_cutoff', { date: cutoffDate }) }}</span>
        {{ $t('performance_dashboard_cutoff_note') }}
        <info-button topic="cutoff" @open="openInfo" />
      </div>

      <!-- Criterio de selección de actividades -->
      <div class="px-4 py-3 bg-white border border-grey-light rounded-lg">
        <h3 class="mb-2 text-sm font-bold text-blue-dark">
          {{ $t('performance_dashboard_selection_title') }}
          <info-button topic="selection" @open="openInfo" />
        </h3>
        <p class="mb-1">
          {{ $t('performance_dashboard_selection_criteria', { weight: performanceThresholds.weight, deviation: performanceThresholds.deviation }) }}
        </p>
        <p class="mb-1">
          {{ $t('performance_dashboard_selection_partial', { budget: fmtCurrency(performanceSummary.total_cost) }) }}
        </p>
        <p class="mb-1">
          <span class="font-bold">{{ performanceSummary.selected }}</span>
          {{ $t('performance_dashboard_selection_counts', { measurable: performanceSummary.measurable, invalid: selectionInvalid }) }}
        </p>

        <a href="#" class="inline-flex items-center gap-1 text-blue-dark hover:underline" @click.prevent="showReasons = !showReasons">
          <b-icon pack="fas" :icon="showReasons ? 'caret-down' : 'caret-right'" size="is-small" />
          {{ $t(showReasons ? 'performance_dashboard_hide_reasons' : 'performance_dashboard_show_reasons') }}
        </a>
        <info-button topic="reasons" @open="openInfo" />

        <!-- Motivo por actividad: en verde el criterio que cumple -->
        <div v-if="showReasons" class="mt-2">
          <div class="epc-data-table">
            <div class="table-wrapper">
              <table class="table is-narrow">
                <thead>
                  <tr>
                    <th class="text-left">{{ $t('activity') }}</th>
                    <th class="w-20">{{ $t('performance_dashboard_reason_critical_path') }}</th>
                    <th class="w-20">{{ $t('performance_dashboard_reasons_col_cost_share') }}</th>
                    <th class="w-48">{{ $t('performance_dashboard_reasons_col_deviation') }}</th>
                    <th class="text-left">{{ $t('performance_dashboard_reason_followup') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!reasonRows.length">
                    <td colspan="5" class="py-4 text-center text-grey-dark">{{ loadingPerformance ? $t('loading') : $t('no_results') }}</td>
                  </tr>
                  <tr v-for="row in reasonRows" :key="row.id">
                    <td>
                      <span class="font-mono text-grey-dark">{{ row.code }}</span>
                      <span class="ml-1 uppercase">{{ row.name }}</span>
                    </td>
                    <td :class="['text-center font-bold', row.meetsCritical ? 'bg-green-lightest text-green-dark' : '']">
                      {{ $t(row.critical ? 'yes' : 'no') }}
                    </td>
                    <td :class="['text-center font-bold', row.meetsWeight ? 'bg-green-lightest text-green-dark' : '']">
                      {{ fmtPercent(row.costShare, 1) }}
                    </td>
                    <td :class="['text-center font-bold', row.meetsDeviation ? 'bg-green-lightest text-green-dark' : '']">
                      {{ fmtSignedPercent(row.costDeviation) }}
                    </td>
                    <td>{{ row.text }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <p class="mt-1 italic text-grey-darker">{{ $t('performance_dashboard_reasons_note') }}</p>
        </div>
      </div>

      <!-- Alcance de la selección -->
      <div class="px-4 py-2 bg-white border border-grey-light rounded-lg">
        <span class="font-bold">{{ performanceSummary.selected }}</span>
        {{ $t('performance_dashboard_scope', {
          total: performanceSummary.candidates,
          cancelled: performanceSummary.cancelled_ignored,
          budget: fmtCurrency(performanceSummary.selected_cost),
          percent: fmtPercent(selectionCoveredPercent, 1),
        }) }}
        <info-button topic="scope" @open="openInfo" />
      </div>

      <!-- Indicadores -->
      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 bg-white border border-grey-light rounded-lg overflow-hidden">
        <div v-for="(kpi, i) in kpis" :key="kpi.label"
          :class="['flex flex-col', { 'border-t md:border-t-0 xl:border-l border-grey-light': i > 0 }]">
          <div class="flex-1 px-4 py-3">
            <div class="font-bold uppercase tracking-wide text-grey-darker">
              {{ kpi.label }}
              <info-button :topic="kpi.info" @open="openInfo" />
            </div>
            <div class="mt-1 text-2xl font-bold text-blue-darkest leading-tight">{{ kpi.value }}</div>
            <div class="mt-1 text-grey-darker">{{ kpi.hint }}</div>
          </div>
          <div v-if="kpi.footer" :class="['px-4 py-1 text-center font-semibold', FOOTER_VARIANTS[kpi.variant]]">{{ kpi.footer }}</div>
        </div>
      </div>

      <!-- 1. A pasado y a futuro, por actividad -->
      <h3 class="mt-1 text-sm font-bold text-blue-darkest">{{ $t('performance_dashboard_section_past_future') }}</h3>

      <div class="grid grid-cols-1 xl:grid-cols-3 gap-3">
        <div class="flex flex-col px-4 py-3 bg-white border border-grey-light rounded-lg">
          <h4 class="text-sm font-bold text-center text-blue-darkest">
            {{ $t('performance_dashboard_progress_to_date') }}
            <info-button topic="chart_progress" @open="openInfo" />
          </h4>
          <p class="mb-2 text-center text-grey-darker">{{ $t('performance_dashboard_progress_to_date_subtitle') }}</p>
          <column-chart
            :data="progressChartData"
            :colors="[COLORS.budgeted, COLORS.planned, COLORS.actual]"
            :min="0" :max="100" suffix="%"
            :library="chartLibrary"
            :messages="{ empty: loadingPerformance ? $t('loading') : $t('performance_dashboard_no_chart_data') }"
            height="300px" />
          <p class="mt-2 text-grey-darker">{{ $t('performance_dashboard_progress_to_date_note') }}</p>
        </div>

        <div class="flex flex-col px-4 py-3 bg-white border border-grey-light rounded-lg">
          <h4 class="text-sm font-bold text-center text-blue-darkest">
            {{ $t('performance_dashboard_staff_to_date') }}
            <info-button topic="chart_staff" @open="openInfo" />
          </h4>
          <p class="mb-2 text-center text-grey-darker">{{ $t('performance_dashboard_staff_to_date_subtitle') }}</p>
          <column-chart
            :data="staffChartData"
            :colors="[COLORS.planned, COLORS.actual]"
            :library="chartLibrary"
            :messages="{ empty: loadingPerformance ? $t('loading') : $t('performance_dashboard_no_chart_data') }"
            height="300px" />
          <p class="mt-2 text-grey-darker">
            {{ $t('performance_dashboard_staff_to_date_note', { actual: staffCoverage.actual, planned: staffCoverage.planned, total: performanceActivities.length }) }}
          </p>
        </div>

        <div class="flex flex-col px-4 py-3 bg-white border border-grey-light rounded-lg">
          <h4 class="text-sm font-bold text-center text-blue-darkest">
            {{ $t('performance_dashboard_weeks_to_finish') }}
            <info-button topic="chart_weeks" @open="openInfo" />
          </h4>
          <p class="mb-2 text-center text-grey-darker">{{ $t('performance_dashboard_weeks_to_finish_subtitle') }}</p>
          <column-chart
            :data="weeksChartData"
            :colors="[COLORS.actual, COLORS.planned, COLORS.remaining]"
            :library="chartLibrary"
            :messages="{ empty: loadingPerformance ? $t('loading') : $t('performance_dashboard_no_chart_data') }"
            height="300px" />
          <p class="mt-2 text-grey-darker">
            {{ $t('performance_dashboard_weeks_available', { rateWeeks: performanceWindows.rate_weeks, planWeeks: performanceWindows.plan_weeks, rate: weeksCoverage.rate, plan: weeksCoverage.plan, end: weeksCoverage.end, total: performanceActivities.length }) }}
          </p>
          <p v-if="weeksCoverage.undated" class="text-grey-darker">
            {{ $t('performance_dashboard_weeks_missing', { count: weeksCoverage.undated }) }}
          </p>
          <p class="text-grey-darker">{{ $t('performance_dashboard_weeks_source') }}</p>
          <p class="mt-1 font-semibold text-red-dark">
            {{ $t('performance_dashboard_weeks_to_finish_note', { overdue: weeksCoverage.overdue }) }}
          </p>
        </div>
      </div>

      <!-- 2. Actividades que requieren atención -->
      <h3 class="mt-1 text-sm font-bold text-blue-darkest">
        {{ $t('performance_dashboard_section_attention') }}
        <info-button topic="attention" @open="openInfo" />
      </h3>

      <div class="epc-data-table">
        <div class="table-wrapper">
          <table class="table is-narrow">
            <thead>
              <tr>
                <th class="text-left">{{ $t('activity') }}</th>
                <th>{{ $t('performance_dashboard_col_weight') }}</th>
                <th>{{ $t('performance_dashboard_col_baseline') }}</th>
                <th>{{ $t('performance_dashboard_col_last_plan') }}</th>
                <th>{{ $t('performance_dashboard_col_actual') }}</th>
                <th>{{ $t('performance_dashboard_col_progress_variance') }}</th>
                <th>{{ $t('measurement') }}</th>
                <th>{{ $t('executed1') }}</th>
                <th>{{ $t('pending') }}</th>
                <th>{{ $t('performance_dashboard_col_rate_4w') }}</th>
                <th>{{ $t('performance_dashboard_col_plan_6w') }}</th>
                <th>{{ $t('end') }}</th>
                <th>{{ $t('performance_dashboard_col_weeks_to_end') }}</th>
                <th>{{ $t('performance_dashboard_col_float') }}</th>
                <th>{{ $t('performance_dashboard_col_critical') }}</th>
                <th>{{ $t('performance_dashboard_col_gross_delay') }}</th>
                <th>{{ $t('performance_dashboard_col_project_delay') }}</th>
                <th>{{ $t('status') }}</th>
                <th>{{ $t('performance_dashboard_col_priority') }}</th>
                <th>{{ $t('action') }}</th>
                <th>{{ $t('manager') }}</th>
                <th>{{ $t('date') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!attentionActivities.length">
                <td colspan="22" class="py-6 text-center text-grey-dark">
                  {{ loadingPerformance ? $t('loading') : $t('no_results') }}
                </td>
              </tr>
              <tr v-for="activity in attentionActivities" :key="activity.id"
                :class="['cursor-pointer', { 'epc-data-table__row--selected': activity.code === selectedActivityCode }]"
                @click="selectActivity(activity.code)">
                <td class="w-64">
                  <div class="font-mono text-grey-dark">{{ activity.code }}</div>
                  <div class="font-bold uppercase text-blue-darkest">{{ activity.name }}</div>
                </td>
                <td class="text-right">{{ fmt(activity.weight) }}</td>
                <td class="text-right">{{ fmt(activity.baseline, 0) }}</td>
                <td class="text-right">{{ fmt(activity.lastPlan, 0) }}</td>
                <td class="text-right font-bold">
                  <span v-if="activity.progressFromField" class="text-grey-dark">F </span>{{ fmt(activity.actual) }}
                </td>
                <td class="text-right font-bold">
                  <span :class="varianceOf(activity) < 0 ? 'text-red-dark' : 'text-green-dark'">{{ fmt(varianceOf(activity)) }}</span>
                </td>
                <td class="w-56" @click.stop>
                  <select v-if="activity.measurements.length"
                    class="w-full px-1 py-0.5 text-xs bg-white border border-grey-light rounded"
                    :value="activity.selectedMeasurementId != null ? String(activity.selectedMeasurementId) : ''"
                    @change="setAttentionMeasurement(activity.id, $event.target.value)">
                    <option v-if="!activity.hasPrimary" value="">—</option>
                    <option v-for="measurement in activity.measurements" :key="measurement.id" :value="String(measurement.id)">
                      {{ measurement.primary ? '★ ' : '' }}{{ measurement.name }}
                    </option>
                  </select>
                  <span v-else>—</span>
                </td>
                <td class="text-right whitespace-nowrap">
                  <template v-if="activity.selectedMeasurementId != null">
                    {{ fmt(activity.executed) }} / {{ fmt(activity.total) }} {{ activity.unit }}
                  </template>
                  <span v-else>—</span>
                </td>
                <td class="text-right whitespace-nowrap">
                  <template v-if="activity.selectedMeasurementId != null">
                    {{ fmt(activity.pending) }} {{ activity.unit }}
                  </template>
                  <span v-else>—</span>
                </td>
                <td class="text-right">{{ fmt(activity.rate4w, 2) }}</td>
                <td class="text-right">{{ fmt(activity.plan6w, 2) }}</td>
                <td class="whitespace-nowrap">{{ fmtDate(activity.endDate) }}</td>
                <td class="text-right font-bold">
                  <span :class="{ 'text-red-dark': activity.weeksToEnd < 0 }">{{ fmt(activity.weeksToEnd) }}</span>
                </td>
                <td class="text-right">{{ fmt(activity.float, 0) }}</td>
                <td class="text-center font-bold">
                  <span :class="{ 'text-red-dark': activity.critical }">{{ $t(activity.critical ? 'yes' : 'no') }}</span>
                </td>
                <td class="text-right">{{ fmt(activity.grossDelay, 0) }}</td>
                <td class="text-right">{{ fmt(activity.projectDelay, 0) }}</td>
                <td>
                  <span v-if="activity.status && STATUSES[activity.status]" :class="['px-2 py-0.5 rounded font-semibold whitespace-nowrap', STATUSES[activity.status].classes]">
                    {{ $t(STATUSES[activity.status].label) }}
                  </span>
                  <span v-else>—</span>
                </td>
                <td class="whitespace-nowrap">
                  <span v-if="activity.priority" class="font-bold text-blue-darkest">{{ activity.priority.toUpperCase() }}</span>
                  <span v-else class="text-grey-dark">{{ $t('performance_dashboard_not_defined') }}</span>
                </td>
                <td class="epc-data-table__cell--highlight w-32">
                  <span v-if="activity.action">{{ activity.action }}</span>
                  <span v-else class="text-grey-dark">{{ $t('performance_dashboard_no_action') }}</span>
                </td>
                <td class="whitespace-nowrap">{{ activity.actionManager || '—' }}</td>
                <td class="whitespace-nowrap">{{ fmtDate(activity.actionDate) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <p class="text-grey-darker">{{ $t('performance_dashboard_attention_sorted') }}</p>
      <p class="text-grey-darker">
        {{ $t('performance_dashboard_attention_available', { defined: performanceSummary.schedule_defined, total: performanceActivities.length }) }}
      </p>
      <p class="text-grey-darker">{{ $t('performance_dashboard_attention_missing') }}</p>
      <p class="text-grey-darker">
        {{ $t('performance_dashboard_attention_origin', { rescheduled: performanceSummary.rescheduled_collapsed, cancelled: performanceSummary.cancelled_ignored }) }}
      </p>
      <p class="text-grey-darker">{{ $t('performance_dashboard_attention_system') }}</p>

      <!-- 3. Ficha de actividad -->
      <div ref="activitySheet" class="flex flex-wrap items-center justify-between gap-2 mt-1">
        <div class="flex flex-wrap items-center gap-2">
          <h3 class="text-sm font-bold text-blue-darkest">{{ $t('performance_dashboard_section_activity_sheet') }}</h3>
          <select v-model="selectedActivityCode" class="px-2 py-1 text-xs bg-white border border-grey-light rounded">
            <option v-for="activity in attentionActivities" :key="activity.id" :value="activity.code">
              {{ activity.code }} · {{ activity.name.toUpperCase() }}
            </option>
          </select>
        </div>
      </div>

      <div v-if="selectedActivity" class="flex flex-col gap-3 px-4 py-3 bg-white border border-grey-light rounded-lg">
        <h3 class="text-base font-bold text-blue-darkest">{{ $t('performance_dashboard_activity_evolution_sheet') }}</h3>

        <div class="flex flex-wrap items-center gap-2 p-1 border border-grey-light rounded">
          <span class="px-2 py-1 rounded bg-blue-darkest text-white font-bold">{{ $t('activity') }}</span>
          <span class="px-2 py-1 rounded bg-yellow-lighter font-mono font-bold">{{ selectedActivity.code }}</span>
          <span class="font-bold uppercase text-blue-darkest">{{ selectedActivity.name }}</span>
          <span class="text-grey-darker">· {{ $t('unit') }}: {{ sheet.unit || '—' }}</span>

          <div class="flex items-center gap-2 ml-auto">
            <span class="text-grey-darker">{{ $t('measurement') }}:</span>
            <select v-if="selectedActivity.measurements.length"
              class="px-1 py-0.5 text-xs bg-white border border-grey-light rounded"
              :value="selectedActivity.selectedMeasurementId != null ? String(selectedActivity.selectedMeasurementId) : ''"
              @change="setAttentionMeasurement(selectedActivity.id, $event.target.value)">
              <option v-if="!selectedActivity.hasPrimary" value="">—</option>
              <option v-for="measurement in selectedActivity.measurements" :key="measurement.id" :value="String(measurement.id)">
                {{ measurement.primary ? '★ ' : '' }}{{ measurement.name }}
              </option>
            </select>
            <span v-else>—</span>
            <span class="px-2 py-1 rounded bg-blue-darkest text-white font-bold">{{ $t('performance_dashboard_col_priority') }}</span>
            <select :value="actionDraft.priority" :disabled="!canEditReview || savingReview"
              :class="['px-2 py-1 text-sm font-semibold bg-white border rounded focus:outline-none', priorityDirty ? 'border-green' : 'border-grey-light']"
              @change="actionDraft.priority = $event.target.value">
              <option value="">{{ $t('performance_dashboard_not_defined') }}</option>
              <option value="p1">P1</option>
              <option value="p2">P2</option>
            </select>
            <button v-if="canEditReview && priorityDirty" type="button" :disabled="savingReview" :title="$t('save')"
              class="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-white rounded bg-green hover:bg-green-dark focus:outline-none"
              @click="saveReview">
              <i class="fas fa-check"></i>
            </button>
          </div>
        </div>

        <p class="text-grey-darker">
          {{ $t('performance_dashboard_sheet_info', {
            cutoff: cutoffDate,
            end: fmtDate(selectedActivity.endDate),
            status: statusLabel(selectedActivity),
          }) }}
        </p>

        <!-- Indicadores de la actividad -->
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          <div v-for="kpi in sheetKpis" :key="kpi.label" class="px-4 py-3 rounded bg-grey-lightest">
            <div class="font-bold uppercase tracking-wide text-grey-darker">
              {{ kpi.label }}
              <info-button :topic="kpi.info" @open="openInfo" />
            </div>
            <div :class="['mt-1 text-2xl font-bold leading-tight', kpi.negative ? 'text-red-dark' : 'text-blue-darkest']">{{ kpi.value }}</div>
            <div class="mt-1 text-grey-darker">{{ kpi.hint }}</div>
          </div>
        </div>

        <!-- A pasado / A futuro -->
        <div class="grid grid-cols-1 xl:grid-cols-2 gap-3">
          <div class="epc-data-table">
            <table class="table is-narrow">
              <thead>
                <tr><th colspan="4">{{ $t('performance_dashboard_past_title') }} <info-button topic="past" @open="openInfo" /></th></tr>
                <tr>
                  <th></th>
                  <th>{{ $t('performance_dashboard_col_budgeted_short') }}</th>
                  <th>{{ $t('performance_dashboard_col_planned_short') }}</th>
                  <th>{{ $t('actual') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in pastRows" :key="row.label">
                  <td class="font-bold">{{ row.label }}</td>
                  <td v-for="(value, i) in row.values" :key="i" class="text-right">
                    <span :class="{ 'font-bold text-red-dark': i === 2 && row.alertActual }">{{ value }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="epc-data-table">
            <table class="table is-narrow">
              <thead>
                <tr><th colspan="5">{{ $t('performance_dashboard_future_title', { weeks: fmt(selectedActivity.weeksToEnd) }) }} <info-button topic="future" @open="openInfo" /></th></tr>
                <tr>
                  <th></th>
                  <th>{{ $t('performance_dashboard_col_remaining_budget') }}</th>
                  <th>{{ $t('performance_dashboard_col_plan_6_weeks') }}</th>
                  <th>{{ $t('performance_dashboard_col_needed') }}</th>
                  <th>{{ $t('performance_dashboard_col_actual_4_weeks') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in futureRows" :key="row.label">
                  <td class="font-bold">{{ row.label }}</td>
                  <td v-for="(value, i) in row.values" :key="i" class="text-right">{{ value }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <p class="italic text-grey-darker">{{ $t('performance_dashboard_sheet_note') }}</p>

        <!-- Situación / causa / acción -->
        <div class="epc-data-table">
          <table class="table is-narrow">
            <tbody>
              <tr>
                <td class="w-40 font-bold">{{ $t('performance_dashboard_situation') }} <info-button topic="situation" @open="openInfo" /></td>
                <td>{{ $t(situationKey) }}</td>
              </tr>
              <tr>
                <td class="font-bold">{{ $t('performance_dashboard_cause') }}</td>
                <td>
                  <div class="flex items-center gap-2">
                    <input v-model="actionDraft.cause" type="text" :disabled="!canEditReview || savingReview"
                      :placeholder="$t('performance_dashboard_not_registered')"
                      :class="['w-full px-3 py-2 text-sm bg-white border rounded focus:outline-none focus:border-blue', causeDirty ? 'border-green' : 'border-grey']">
                    <button v-if="canEditReview && causeDirty" type="button" :disabled="savingReview" :title="$t('save')"
                      class="inline-flex items-center justify-center flex-shrink-0 w-9 h-9 text-white rounded bg-green hover:bg-green-dark focus:outline-none"
                      @click="saveReview">
                      <i class="fas fa-check"></i>
                    </button>
                  </div>
                </td>
              </tr>
              <tr>
                <td class="epc-data-table__cell--highlight font-bold">{{ $t('performance_dashboard_agreed_action') }}</td>
                <td class="epc-data-table__cell--highlight">
                  <template v-if="selectedActivity.action">
                    {{ selectedActivity.action }}
                    <span class="text-grey-darker">
                      · {{ selectedActivity.actionManager || '—' }}
                      · {{ fmtDate(selectedActivity.actionDate) }}
                      · {{ $t(ACTION_STATUSES[selectedActivity.actionStatus] || 'pending') }}
                    </span>
                  </template>
                  <span v-else class="text-grey-dark">{{ $t('performance_dashboard_no_action') }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Últimas 6 semanas del proyecto: barras verticales de presupuestado, planificado y real -->
        <p>
          <span class="font-bold text-blue-darkest">{{ $t('performance_dashboard_last_weeks', { weeks: selectedActivity.weeklyProgress.length || weeklyWindow.length }) }}</span>
          <span class="text-grey-darker">· {{ $t('performance_dashboard_last_weeks_note') }}</span>
        </p>
        <div class="grid grid-cols-1 xl:grid-cols-3 gap-3">
          <div v-for="chart in weeklyCharts" :key="chart.title" class="px-3 py-2 border border-grey-light rounded">
            <h4 class="mb-1 text-sm font-bold text-center text-blue-darkest">{{ chart.title }} <info-button :topic="chart.info" @open="openInfo" /></h4>
            <column-chart
              :data="chart.data"
              :colors="chart.colors"
              :library="chartLibrary"
              :messages="chartMessages"
              v-bind="chart.scale"
              height="200px" />
            <p v-if="chart.note" class="mt-1 text-center text-grey-darker">{{ chart.note }}</p>
          </div>
        </div>

        <!-- Histórico mensual -->
        <p>
          <span class="font-bold text-blue-darkest">{{ $t('performance_dashboard_history_title', { start: workMonthOrigin }) }}</span>
          <span class="block text-grey-darker">{{ $t('performance_dashboard_history_note', { cutoff: cutoffDate }) }}</span>
        </p>
        <div class="grid grid-cols-1 xl:grid-cols-3 gap-3">
          <div v-for="chart in historyCharts" :key="chart.title" class="px-3 py-2 border border-grey-light rounded">
            <h4 class="mb-1 text-sm font-bold text-center text-blue-darkest">{{ chart.title }} <info-button :topic="chart.info" @open="openInfo" /></h4>
            <line-chart :data="chart.data" :colors="chart.colors" :library="historyLibrary" :messages="chartMessages" height="220px" />
          </div>
        </div>

        <!-- Evolución mensual: desde el inicio; si hay más de 12 meses de obra, solo los últimos 12 -->
        <p class="font-bold text-blue-darkest">{{ $t('performance_dashboard_monthly_title') }} <info-button topic="monthly" @open="openInfo" /></p>
        <div class="epc-data-table">
          <div class="table-wrapper">
            <table class="table is-narrow">
              <thead>
                <tr>
                  <th rowspan="2" class="text-left">{{ $t('performance_dashboard_col_work_month') }}</th>
                  <th colspan="3">{{ $t('performance_dashboard_col_progress_unit', { unit: sheet.unit }) }}</th>
                  <th colspan="2">{{ $t('performance_dashboard_col_cumulative_progress') }}</th>
                  <th colspan="3">{{ $t('performance_dashboard_row_staff') }}</th>
                  <th colspan="3">{{ $t('performance_dashboard_row_yield', { unit: sheet.unit }) }}</th>
                  <th>{{ $t('performance_dashboard_col_losses') }}</th>
                </tr>
                <tr>
                  <th>{{ $t('performance_dashboard_col_budgeted_short') }}</th>
                  <th>{{ $t('performance_dashboard_col_planned_short') }}</th>
                  <th>{{ $t('actual') }}</th>
                  <th>{{ $t('performance_dashboard_col_budgeted_short') }}</th>
                  <th>{{ $t('actual') }}</th>
                  <th>{{ $t('performance_dashboard_col_budgeted_short') }}</th>
                  <th>{{ $t('performance_dashboard_col_planned_short') }}</th>
                  <th>{{ $t('actual') }}</th>
                  <th>{{ $t('performance_dashboard_col_budgeted_short') }}</th>
                  <th>{{ $t('performance_dashboard_col_planned_short') }}</th>
                  <th>{{ $t('actual') }}</th>
                  <th>{{ $t('performance_dashboard_col_lost_hours') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!monthlyEvolution.length">
                  <td colspan="13" class="text-grey-dark">{{ $t('performance_dashboard_no_chart_data') }}</td>
                </tr>
                <tr v-for="month in monthlyEvolution" :key="month.number">
                  <td class="whitespace-nowrap font-bold">{{ month.number }}</td>
                  <td class="text-right">{{ fmt(month.quantity.budgeted) }}</td>
                  <td class="text-right">{{ fmt(month.quantity.planned) }}</td>
                  <td class="text-right"><span :class="realClass(month.quantity.actual)">{{ fmt(month.quantity.actual) }}</span></td>
                  <td class="text-right">{{ fmtPercent(month.cumulative.budgeted) }}</td>
                  <td class="text-right"><span :class="realClass(month.cumulative.actual)">{{ fmtPercent(month.cumulative.actual) }}</span></td>
                  <td class="text-right">{{ fmt(month.staff.budgeted, 1) }}</td>
                  <td class="text-right">{{ fmt(month.staff.planned, 1) }}</td>
                  <td class="text-right">{{ fmt(month.staff.actual, 1) }}</td>
                  <td class="text-right">{{ fmt(month.yield.budgeted, 1) }}</td>
                  <td class="text-right">{{ fmt(month.yield.planned, 1) }}</td>
                  <td class="text-right"><span :class="realClass(month.yield.actual)">{{ fmt(month.yield.actual, 1) }}</span></td>
                  <td class="text-right">{{ fmtPercent(month.lostHours) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <p class="text-grey-darker">{{ $t('performance_dashboard_monthly_note') }}</p>
      </div>

      <!-- Detalle de mediciones y series del sistema -->
      <div v-if="selectedActivity" class="bg-white border border-grey-light rounded-lg">
        <a href="#" class="flex items-center gap-1 px-4 py-2 font-bold text-blue-darkest" @click.prevent="showSystemDetail = !showSystemDetail">
          <b-icon pack="fas" :icon="showSystemDetail ? 'caret-down' : 'caret-right'" size="is-small" />
          {{ $t('performance_dashboard_system_detail_title') }}
          <info-button topic="system_kpis" @open="openInfo" />
        </a>

        <div v-if="showSystemDetail" class="flex flex-col gap-3 px-4 pb-4">
          <div class="flex flex-wrap items-center justify-between gap-2 pb-2 border-b border-grey-light">
            <div class="flex flex-wrap items-center gap-2">
              <span class="font-mono text-grey-dark">{{ selectedActivity.code }}</span>
              <span class="font-bold uppercase text-blue-darkest">· {{ selectedActivity.name }}</span>
              <span v-if="selectedActivity.status && STATUSES[selectedActivity.status]"
                :class="['px-2 py-0.5 rounded font-semibold whitespace-nowrap', STATUSES[selectedActivity.status].classes]">
                {{ statusLabel(selectedActivity) }}
              </span>
              <span v-else class="text-grey-dark">{{ $t('performance_dashboard_not_defined') }}</span>
            </div>
            <span class="text-grey-darker">
              {{ $t('performance_dashboard_system_budget_end', { budget: systemDetail.budget == null ? '—' : fmtCurrency(systemDetail.budget), date: fmtDate(selectedActivity.endDate) }) }}
            </span>
          </div>

          <div class="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-px bg-grey-light border border-grey-light rounded overflow-hidden">
            <div v-for="kpi in systemKpis" :key="kpi.label" class="px-3 py-2 bg-white">
              <div class="font-bold uppercase tracking-wide text-grey-darker">{{ kpi.label }}</div>
              <div :class="['mt-1 text-lg font-bold leading-tight', kpi.negative ? 'text-red-dark' : 'text-blue-darkest']">
                {{ kpi.value }}<sup v-if="kpi.mark" class="text-grey-dark">°</sup>
              </div>
              <div class="mt-1 text-grey-darker">{{ kpi.hint }}</div>
            </div>
          </div>

          <!-- Mediciones de la actividad -->
          <p>
            <span class="font-bold text-blue-darkest">{{ $t('performance_dashboard_system_measurements_title') }}</span>
            <info-button topic="system_measurements" @open="openInfo" />
            <span class="text-grey-darker">— {{ $t('performance_dashboard_system_measurements_subtitle') }}</span>
          </p>
          <div class="epc-data-table">
            <div class="table-wrapper">
              <table class="table is-narrow">
                <thead>
                  <tr>
                    <th></th>
                    <th>{{ $t('measurement') }}</th>
                    <th>{{ $t('origin') }}</th>
                    <th>{{ $t('unit') }}</th>
                    <th>{{ $t('planned') }}</th>
                    <th>{{ $t('performance_dashboard_system_col_executed_cutoff') }}</th>
                    <th>{{ $t('pending') }}</th>
                    <th>{{ $t('performance_dashboard_system_col_progress') }}</th>
                    <th>{{ $t('performance_dashboard_col_rate_4w') }}</th>
                    <th>{{ $t('performance_dashboard_col_plan_6w') }}</th>
                    <th>{{ $t('performance_dashboard_system_col_weeks_to_finish') }}</th>
                    <th>{{ $t('performance_dashboard_system_col_actual_yield') }}</th>
                    <th>{{ $t('performance_dashboard_system_col_planned_hours') }}</th>
                    <th>{{ $t('performance_dashboard_system_col_planned_yield') }}</th>
                    <th>{{ $t('performance_dashboard_system_col_warnings') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="m in systemDetail.measurements" :key="m.id"
                    :class="['cursor-pointer', { 'epc-data-table__row--selected': m.id === systemMeasurement.id }]"
                    @click="selectSystemMeasurement(m.id)">
                    <td class="text-center">
                      <input type="radio" name="system-measurement" class="cursor-pointer" :checked="m.id === systemMeasurement.id">
                    </td>
                    <td class="w-64">
                      <div :class="{ 'font-bold': m.origin === 'budget' }">
                        {{ m.primary ? '★ ' : '' }}{{ m.name }}
                      <span v-if="m.parts > 1" class="font-normal text-grey-darker">· {{ $t('performance_dashboard_measurement_parts', { count: m.parts }) }}</span>
                      </div>
                      <div class="font-mono text-grey-dark">{{ m.code }}</div>
                    </td>
                    <td class="whitespace-nowrap">{{ $t('performance_dashboard_system_origin_' + m.origin) }}</td>
                    <td>{{ m.unit }}</td>
                    <td class="text-right">{{ fmt(m.total) }}</td>
                    <td class="text-right">{{ fmt(m.executed) }}</td>
                    <td class="text-right">{{ fmt(m.pending) }}</td>
                    <td class="text-right">{{ fmt(m.progress) }}</td>
                    <td class="text-right">{{ fmt(m.rate4w, 2) }}</td>
                    <td class="text-right">{{ fmt(m.plan6w, 2) }}</td>
                    <td class="text-right">{{ fmt(m.weeksToFinish) }}</td>
                    <td class="text-right">{{ fmt(m.yieldActual, 2) }}</td>
                    <td class="text-right">{{ fmt(m.plannedHours, 0) }}</td>
                    <td class="text-right">{{ fmt(m.yieldPlanned, 2) }}</td>
                    <td class="whitespace-nowrap">
                      <span v-if="m.undated" class="text-orange-dark">
                        {{ $t('performance_dashboard_system_warning_undated', { quantity: fmt(m.undated), unit: m.unit }) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <p class="text-grey-darker">{{ $t('performance_dashboard_system_measurements_note') }}</p>

          <!-- Evolución de la medición seleccionada -->
          <div class="flex flex-col gap-2 px-3 py-2 border border-grey-light rounded">
            <div class="flex flex-wrap items-center gap-2">
              <span class="font-bold text-blue-darkest">
                {{ $t('performance_dashboard_system_evolution_title', { measurement: systemMeasurement.name.toUpperCase() }) }}
                <info-button topic="system_series" @open="openInfo" />
              </span>
              <div class="inline-flex overflow-hidden border border-blue-dark rounded">
                <button v-for="mode in SERIES_MODES" :key="mode" type="button"
                  :class="['px-2 py-0.5 focus:outline-none', seriesMode === mode ? 'bg-blue-dark text-white' : 'bg-white text-blue-dark hover:bg-blue-lightest']"
                  @click="seriesMode = mode">
                  {{ $t(mode) }}
                </button>
              </div>
            </div>
            <p class="text-grey-darker">
              {{ $t('performance_dashboard_system_evolution_note', { quantity: fmt(systemMeasurement.undated), unit: systemMeasurement.unit }) }}
            </p>
            <column-chart
              :data="seriesChartData"
              :colors="[COLORS.planned, COLORS.actual]"
              :library="chartLibrary"
              :messages="{ empty: $t('performance_dashboard_system_no_dated_data') }"
              height="220px" />
          </div>

          <div class="epc-data-table">
            <div class="table-wrapper">
              <table class="table is-narrow">
                <thead>
                  <tr>
                    <th>{{ $t('period') }}</th>
                    <th v-for="period in systemSeries" :key="period.label">{{ period.label }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in seriesRows" :key="row.key">
                    <td class="font-bold whitespace-nowrap">{{ row.label }}</td>
                    <td v-for="period in systemSeries" :key="period.label" class="text-right">{{ fmt(period[row.key]) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Simulación -->
      <div v-if="selectedActivity" class="overflow-hidden bg-white border border-grey-light rounded-lg">
        <div class="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-white bg-blue-darkest">
          <span class="font-bold">
            {{ $t('performance_dashboard_simulation_title', { code: selectedActivity.code }) }}
            <info-button topic="simulation" light @open="openInfo" />
          </span>
          <a href="#" class="inline-flex items-center gap-1 text-white hover:underline" @click.prevent="resetSimulation">
            <b-icon pack="fas" icon="rotate-left" size="is-small" />
            {{ $t('performance_dashboard_simulation_restore') }}
          </a>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label v-for="field in simulationFields" :key="field.key" class="flex flex-col gap-1">
              <span class="text-grey-darker">{{ field.label }}</span>
              <input v-model.number="simulation[field.key]" type="number" :step="field.step" min="0"
                :placeholder="'— ' + $t('performance_dashboard_sim_no_data')"
                class="px-2 py-1 text-xs bg-white border border-grey-light rounded focus:outline-none focus:border-blue">
            </label>
            <label class="flex flex-col gap-1">
              <span class="text-grey-darker">{{ $t('end_date_label') }}</span>
              <input v-model="simulation.endDate" type="date"
                class="px-2 py-1 text-xs bg-white border border-grey-light rounded focus:outline-none focus:border-blue">
            </label>
          </div>

          <div class="flex flex-col">
            <div v-for="result in simulationResults" :key="result.label"
              class="flex items-center justify-between gap-2 py-1 border-b border-grey-lighter">
              <span>{{ result.label }}</span>
              <span v-if="result.value !== null" :class="['font-bold', result.negative ? 'text-red-dark' : 'text-blue-darkest']">
                {{ fmt(result.value, result.decimals) }}
              </span>
              <span v-else class="text-grey-dark">— {{ result.reason }}</span>
            </div>
            <p class="mt-2 text-grey-darker">{{ $t('performance_dashboard_sim_formula', { hours: fmt(hoursPerWeek, 0) }) }}</p>
          </div>
        </div>
      </div>

      <!-- Acción acordada -->
      <div v-if="selectedActivity" class="overflow-hidden bg-white border border-grey-light rounded-lg">
        <div class="px-4 py-2 font-bold text-white bg-blue-darkest">
          {{ $t('performance_dashboard_agreed_action') }}
          <info-button topic="agreed_action" light @open="openInfo" />
        </div>
        <form class="grid grid-cols-1 md:grid-cols-12 items-end gap-3 p-4" @submit.prevent="saveReview">
          <label class="flex flex-col gap-1 md:col-span-4">
            <span class="text-grey-darker">{{ $t('action') }}</span>
            <input v-model="actionDraft.action" type="text" :disabled="!canEditReview || savingReview" :placeholder="$t('performance_dashboard_action_placeholder')"
              class="px-2 py-1 text-xs bg-white border border-grey-light rounded focus:outline-none focus:border-blue">
          </label>
          <label class="flex flex-col gap-1 md:col-span-3">
            <span class="text-grey-darker">{{ $t('manager') }}</span>
            <input v-model="actionDraft.manager" type="text" :disabled="!canEditReview || savingReview"
              class="px-2 py-1 text-xs bg-white border border-grey-light rounded focus:outline-none focus:border-blue">
          </label>
          <label class="flex flex-col gap-1 md:col-span-2">
            <span class="text-grey-darker">{{ $t('date') }}</span>
            <input v-model="actionDraft.date" type="date" :disabled="!canEditReview || savingReview"
              class="px-2 py-1 text-xs bg-white border border-grey-light rounded focus:outline-none focus:border-blue">
          </label>
          <label class="flex flex-col gap-1 md:col-span-2">
            <span class="text-grey-darker">{{ $t('status') }}</span>
            <select v-model="actionDraft.status" :disabled="!canEditReview || savingReview"
              class="px-2 py-1 text-xs bg-white border border-grey-light rounded focus:outline-none focus:border-blue">
              <option v-for="(label, status) in ACTION_STATUSES" :key="status" :value="status">{{ $t(label) }}</option>
            </select>
          </label>
          <button type="submit" :disabled="!canEditReview || savingReview"
            :class="['md:col-span-1 px-3 py-1 font-semibold text-white rounded bg-blue-dark focus:outline-none', canEditReview && !savingReview ? 'hover:bg-blue-darker' : 'opacity-50 cursor-not-allowed']">
            {{ $t('save') }}
          </button>
        </form>
      </div>
    </div>

    <!-- Detalle técnico del bloque (icono «i») -->
    <b-modal :active="infoDetail !== null" has-modal-card @close="infoTopic = null">
      <div v-if="infoDetail" class="modal-card" style="width: 760px; max-width: 95vw">
        <header class="modal-card-head">
          <p class="modal-card-title text-base font-bold">{{ infoDetail.title }}</p>
          <button type="button" class="delete" @click="infoTopic = null" />
        </header>
        <section class="modal-card-body">
          <template v-if="infoDetail.summary">
            <h4 class="mb-1 text-sm font-bold text-blue-darkest">{{ PERFORMANCE_INFO_SECTIONS.summary }}</h4>
            <p class="mb-4 text-sm leading-relaxed whitespace-pre-line text-grey-darkest">{{ infoDetail.summary }}</p>
          </template>
          <h4 class="mb-1 text-sm font-bold text-blue-darkest">{{ PERFORMANCE_INFO_SECTIONS.detail }}</h4>
          <pre class="p-0 text-xs leading-relaxed whitespace-pre-wrap bg-white text-grey-darkest">{{ infoDetail.text }}</pre>
        </section>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Vue from 'vue'
import axios from 'axios'
import moment from 'moment'
import { mapGetters } from 'vuex'
import VueChartkick from 'vue-chartkick'
import Chart from 'chart.js'
import PageHeader from '·/components/page-header'
import { buildProjectWeeks, findWeekIndexForDate } from './activities/weekly/weeklyProjectWeeks.js'
import { resolveWorkDays } from './activities/weekly/projectCalendarUtils.js'
import { PERFORMANCE_INFO, PERFORMANCE_INFO_SECTIONS, PERFORMANCE_INFO_SUMMARY, PERFORMANCE_INFO_TITLE } from './dashboard-performance-info'

Vue.use(VueChartkick, { adapter: Chart })

// Icono «i» que abre el detalle técnico del bloque (dashboard-performance-info.js)
const InfoButton = {
  name: 'InfoButton',
  props: {
    topic: { type: String, required: true },
    light: { type: Boolean, default: false },
  },
  render(h) {
    return h('button', {
      attrs: { type: 'button', title: PERFORMANCE_INFO_TITLE },
      class: ['inline-flex items-center ml-1 align-middle focus:outline-none', this.light ? 'text-white hover:text-blue-lighter' : 'text-blue hover:text-blue-darker'],
      on: {
        click: (event) => {
          event.preventDefault()
          event.stopPropagation()
          this.$emit('open', this.topic)
        },
      },
    }, [h('i', { class: 'fas fa-info-circle' })])
  },
}

const COLORS = {
  budgeted: '#9ca3af',
  planned: '#3a6bc8',
  actual: '#e8803a',
  remaining: '#e3342f',
}

const FOOTER_VARIANTS = {
  warning: 'bg-yellow-lightest text-yellow-darker',
  success: 'bg-green-lightest text-green-dark',
  danger: 'bg-red-lightest text-red-dark',
}

const STATUSES = {
  completed: { label: 'completed', classes: 'bg-green-lightest text-green-dark' },
  completed_with_pending: { label: 'performance_dashboard_status_completed_with_pending', classes: 'bg-yellow-lightest text-yellow-darker' },
  overdue_no_deadline: { label: 'performance_dashboard_status_overdue_no_deadline', classes: 'bg-red-lightest text-red-dark' },
  in_progress_no_rate: { label: 'performance_dashboard_status_in_progress_no_rate', classes: 'bg-blue-lightest text-blue-dark' },
  in_progress: { label: 'advance_status_in_progress', classes: 'bg-blue-lightest text-blue-dark' },
}

// estado → clave de traducción
const ACTION_STATUSES = {
  pending: 'pending',
  in_progress: 'advance_status_in_progress',
  completed: 'completed',
}

const SERIES_MODES = ['weekly', 'monthly']

// project.configs.progress_type_mode → clave de traducción del modo mostrado en el subtítulo
const PROGRESS_MODES = {
  activity: 'performance_dashboard_mode_activity',
  subactivity: 'performance_dashboard_mode_subactivity',
  takeoff: 'takeoff',
}

// Jornada por defecto (h/día) si el proyecto no define configs.man_hour, igual que la planificación semanal
const DEFAULT_MAN_HOUR = 8

const emptyReview = () => ({ priority: '', cause: '', action: '', manager: '', date: null, status: 'pending' })

// '' / null / NaN → null (inputs numéricos vacíos)
const toNumber = value => (value === '' || value === null || value === undefined || Number.isNaN(Number(value)) ? null : Number(value))

export default {
  middleware: 'auth',
  title: 'project_home_page_title',
  subtitle: 'project_home_page_subtitle',
  menuOption: '2-1',
  hidePageTitle: true,

  components: {
    PageHeader,
    InfoButton,
  },

  data: () => ({
    PERFORMANCE_INFO_SECTIONS,
    COLORS,
    FOOTER_VARIANTS,
    STATUSES,
    ACTION_STATUSES,
    SERIES_MODES,
    showReasons: false,
    showSystemDetail: true,
    seriesMode: 'weekly',

    // Actividades padre de la semana seleccionadas por el criterio, desde el backend
    performanceActivities: [],
    performanceWindows: { rate_weeks: 4, plan_weeks: 6 },
    performanceThresholds: { weight: null, deviation: null },
    performanceSummary: {
      candidates: 0,
      measurable: 0,
      selected: 0,
      total_cost: 0,
      selected_cost: 0,
      schedule_defined: 0,
      cancelled_ignored: 0,
      rescheduled_collapsed: 0,
    },
    // id de actividad → id de la medición padre elegida (por defecto la primaria, si existe)
    attentionMeasurementIds: {},
    loadingPerformance: false,

    selectedActivityCode: null,
    actionDraft: emptyReview(),
    savingReview: false,
    simulation: { pending: null, nextRate: null, planRate: null, nextYield: null, float: null, endDate: null },
    // Bloque cuyo detalle técnico está abierto (clave de PERFORMANCE_INFO)
    infoTopic: null,
  }),

  computed: {
    ...mapGetters({
      project: 'app/project',
    }),

    cutoffDate() {
      return moment().format('DD/MM/YYYY')
    },

    headerSubtitle() {
      const mode = this.project && this.project.configs && this.project.configs.progress_type_mode
      const subtitle = this.$t('performance_dashboard_review_subtitle')
      return PROGRESS_MODES[mode]
        ? `${subtitle} · ${this.$t('performance_dashboard_review_mode', { mode: this.$t(PROGRESS_MODES[mode]) })}`
        : subtitle
    },

    // Mismo cálculo de semanas que la planificación semanal (weekly.vue)
    projectWeeks() {
      if (!this.project) return []
      return buildProjectWeeks(this.project.current_start_date, this.project.current_end_date)
    },

    // Las 6 semanas del proyecto que terminan en la semana de corte (S7, S8…)
    weeklyWindow() {
      const weeks = this.projectWeeks
      const current = this.projectWeek
      if (!weeks.length || !current) return []
      const end = weeks.findIndex(week => week.number === current.number && week.year === current.year)
      const last = end === -1 ? weeks.length - 1 : end
      return weeks.slice(Math.max(0, last - 5), last + 1)
    },

    // Mes 1 del histórico: el mes en que empieza el proyecto
    workMonthOrigin() {
      const start = this.project && this.project.current_start_date
      return start ? moment(start).format('MM/YYYY') : '—'
    },

    // Semana que contiene la fecha de corte (hoy); si el proyecto ya terminó, la última
    projectWeek() {
      const weeks = this.projectWeeks
      if (!weeks.length) return null
      const today = moment()
      if (today.isAfter(weeks[weeks.length - 1].end, 'day')) return weeks[weeks.length - 1]
      return weeks[findWeekIndexForDate(weeks, today)]
    },

    infoDetail() {
      const detail = this.infoTopic ? PERFORMANCE_INFO[this.infoTopic] : null
      return detail ? Object.assign({ summary: PERFORMANCE_INFO_SUMMARY[this.infoTopic] || '' }, detail) : null
    },

    // Fila de la tabla de motivos: cada criterio con su valor y si lo cumple
    reasonRows() {
      return this.attentionActivities.map((activity) => {
        const reasons = activity.reasons || []
        const texts = reasons.map((reason) => {
          if (reason === 'weight') return this.$t('performance_dashboard_reason_text_weight', { share: this.fmtPercent(activity.weight, 1) })
          if (reason === 'cost_deviation') return this.$t('performance_dashboard_reason_text_cost_deviation', { deviation: this.fmtSignedPercent(activity.costDeviation) })
          return this.$t('performance_dashboard_reason_text_' + reason)
        })
        return {
          id: activity.id,
          code: activity.code,
          name: activity.name,
          critical: !!activity.critical,
          costShare: activity.weight,
          costDeviation: activity.costDeviation,
          meetsCritical: reasons.includes('critical_path'),
          meetsWeight: reasons.includes('weight'),
          meetsDeviation: reasons.includes('cost_deviation'),
          text: texts.join(', '),
        }
      })
    },

    selectionInvalid() {
      return Math.max(0, (this.performanceSummary.candidates || 0) - (this.performanceSummary.measurable || 0))
    },

    selectionCoveredPercent() {
      const total = this.performanceSummary.total_cost
      return total > 0 ? (this.performanceSummary.selected_cost * 100) / total : null
    },

    // Indicadores de cabecera sobre las actividades seleccionadas y sus revisiones guardadas
    indicators() {
      const activities = this.attentionActivities
      const today = moment().format('YYYY-MM-DD')
      const withDelay = activities.filter(activity => activity.sortDelay !== null && activity.sortDelay !== undefined)
      const worst = withDelay.reduce((max, activity) => (max === null || activity.sortDelay > max.sortDelay ? activity : max), null)
      const withAction = activities.filter(activity => activity.action)
      const critical = activities.filter(activity => activity.critical)
      return {
        maxDelay: {
          days: worst ? worst.sortDelay : null,
          activity: worst ? `${worst.code} · ${worst.name}` : '',
          withoutData: activities.length - withDelay.length,
          total: activities.length,
        },
        overdueActions: {
          count: withAction.filter(activity => activity.actionDate && activity.actionDate < today && activity.actionStatus !== 'completed').length,
          registered: withAction.length,
        },
        criticalPath: {
          count: critical.length,
          withoutFloat: activities.filter(activity => activity.float === null || activity.float === undefined).length,
        },
        priority: {
          p1: activities.filter(activity => activity.priority === 'p1').length,
          p2: activities.filter(activity => activity.priority === 'p2').length,
          total: activities.length,
        },
      }
    },

    kpis() {
      const { maxDelay, overdueActions, criticalPath, priority } = this.indicators
      const prioritized = priority.p1 + priority.p2
      return [
        {
          label: this.$t('performance_dashboard_max_delay'),
          info: 'kpi_max_delay',
          value: maxDelay.days === null ? '—' : '+' + this.$t('performance_dashboard_days', { count: maxDelay.days }),
          hint: maxDelay.activity,
          footer: maxDelay.withoutData
            ? this.$t('performance_dashboard_partial_without_data', { count: maxDelay.withoutData, total: maxDelay.total })
            : this.$t('performance_dashboard_complete_data'),
          variant: maxDelay.withoutData ? 'warning' : 'success',
        },
        {
          label: this.$t('performance_dashboard_overdue_actions'),
          info: 'kpi_overdue_actions',
          value: this.fmt(overdueActions.count, 0),
          hint: this.$t('performance_dashboard_registered_actions', { count: overdueActions.registered }),
          footer: !overdueActions.registered
            ? this.$t('performance_dashboard_no_actions')
            : overdueActions.count
              ? this.$t('performance_dashboard_overdue_count', { count: overdueActions.count })
              : this.$t('performance_dashboard_actions_up_to_date'),
          variant: !overdueActions.registered ? 'warning' : (overdueActions.count ? 'danger' : 'success'),
        },
        {
          label: this.$t('performance_dashboard_critical_path'),
          info: 'kpi_critical_path',
          value: this.fmt(criticalPath.count, 0),
          hint: this.$t('performance_dashboard_without_float', { count: criticalPath.withoutFloat }),
          footer: criticalPath.withoutFloat
            ? this.$t('performance_dashboard_floats_incomplete')
            : this.$t('performance_dashboard_floats_complete'),
          variant: criticalPath.withoutFloat ? 'warning' : 'success',
        },
        {
          label: this.$t('performance_dashboard_priority'),
          info: 'kpi_priority',
          value: this.fmt(prioritized, 0),
          hint: this.$t('performance_dashboard_priority_split', { p1: priority.p1, p2: priority.p2 }),
          footer: prioritized < priority.total
            ? this.$t('performance_dashboard_without_priority', { count: priority.total - prioritized })
            : this.$t('performance_dashboard_complete_data'),
          variant: prioritized < priority.total ? 'warning' : 'success',
        },
      ]
    },

    // Presupuestado = línea base (fechas programadas), Planificado = última planificación, Real = avance de la actividad
    progressChartData() {
      if (!this.performanceActivities.length) return []
      return [
        { name: this.$t('performance_dashboard_budgeted'), data: this.performanceSeriesOf(a => a.progress.budgeted) },
        { name: this.$t('planned'), data: this.performanceSeriesOf(a => a.progress.planned) },
        { name: this.$t('actual'), data: this.performanceSeriesOf(a => a.progress.actual) },
      ]
    },

    // Personas medias a origen; el presupuestado no existe en el sistema y no se dibuja
    staffChartData() {
      if (!this.staffCoverage.actual && !this.staffCoverage.planned) return []
      return [
        { name: this.$t('planned'), data: this.performanceSeriesOf(a => a.staff.planned) },
        { name: this.$t('actual'), data: this.performanceSeriesOf(a => a.staff.actual) },
      ]
    },

    staffCoverage() {
      return {
        actual: this.performanceActivities.filter(a => a.staff.actual !== null).length,
        planned: this.performanceActivities.filter(a => a.staff.planned !== null).length,
      }
    },

    weeksCoverage() {
      const activities = this.performanceActivities
      const weeksOf = activity => activity.weeks || {}
      const frontOf = activity => activity.front || {}
      return {
        rate: activities.filter(activity => frontOf(activity).rate_4w !== null && frontOf(activity).rate_4w !== undefined).length,
        plan: activities.filter(activity => frontOf(activity).plan_6w !== null && frontOf(activity).plan_6w !== undefined).length,
        end: activities.filter(activity => weeksOf(activity).remaining !== null && weeksOf(activity).remaining !== undefined).length,
        undated: activities.filter(activity => activity.undated_progress).length,
        overdue: activities.filter(activity => weeksOf(activity).remaining < 0).length,
      }
    },

    weeksChartData() {
      const hasValues = this.performanceActivities.some((activity) => {
        const weeks = activity.weeks || {}
        return weeks.current !== null && weeks.current !== undefined
          || weeks.plan !== null && weeks.plan !== undefined
          || weeks.remaining !== null && weeks.remaining !== undefined
      })
      if (!hasValues) return []
      return [
        { name: this.$t('performance_dashboard_current_rate', { weeks: this.performanceWindows.rate_weeks }), data: this.performanceSeriesOf(a => a.weeks.current) },
        { name: this.$t('performance_dashboard_plan_rate', { weeks: this.performanceWindows.plan_weeks }), data: this.performanceSeriesOf(a => a.weeks.plan) },
        {
          name: this.$t('performance_dashboard_remaining_weeks_plan'),
          data: this.performanceSeriesOf(a => a.weeks.remaining),
          // Raya horizontal (sin línea entre puntos) superpuesta a las barras
          dataset: {
            type: 'line',
            showLine: false,
            fill: false,
            pointStyle: 'line',
            pointRadius: 14,
            pointHoverRadius: 14,
            borderWidth: 2,
            borderColor: COLORS.remaining,
            backgroundColor: COLORS.remaining,
          },
        },
      ]
    },

    chartLibrary() {
      return {
        legend: { display: true, position: 'bottom', labels: { boxWidth: 10, fontSize: 10 } },
        scales: {
          xAxes: [{ ticks: { fontSize: 9, maxRotation: 0, autoSkip: false } }],
          yAxes: [{ ticks: { fontSize: 10 } }],
        },
      }
    },

    chartMessages() {
      return { empty: this.$t('performance_dashboard_no_chart_data') }
    },

    // Las mismas actividades de las gráficas, ordenadas por el retraso del frente primario (sin dato al final).
    // Ejecutado, pendiente, ritmos y retraso de la fila salen de la medición padre elegida en el select.
    attentionActivities() {
      return this.performanceActivities
        .map((activity) => {
          const measurements = activity.measurements || []
          const selectedId = this.attentionMeasurementIds[activity.id]
          const selected = measurements.find((measurement) => measurement.measurement_id === selectedId) || null
          const weeks = activity.weeks || {}
          const progress = activity.progress || {}
          const review = activity.review || {}
          const mapped = {
            id: activity.id,
            code: activity.code,
            name: activity.name,
            reasons: activity.reasons || [],
            cost: activity.cost,
            weight: activity.cost_share,
            costDeviation: activity.cost_deviation,
            baseline: progress.budgeted,
            lastPlan: progress.planned,
            actual: progress.actual,
            progressFromField: !!activity.progress_from_field,
            measurements: measurements.map((measurement) => ({
              id: measurement.measurement_id,
              code: measurement.code || '',
              name: measurement.name,
              primary: !!measurement.primary,
              unit: measurement.unit || '',
              executed: measurement.executed,
              total: measurement.total,
              pending: measurement.pending,
              rate4w: measurement.rate_4w,
              plan6w: measurement.plan_6w,
              undated: measurement.undated_progress ? measurement.executed : 0,
              fromBoq: measurement.boq_root_id != null,
              parts: measurement.parts || 1,
              hoursRegistered: measurement.hours_registered,
              yieldActual: measurement.yield_actual,
              yield4w: measurement.yield_4w,
              plannedHours: measurement.planned_hours,
              yieldPlanned: measurement.yield_planned,
              weeklyYield: measurement.weekly_yield || [],
              series: measurement.series || [],
            })),
            hasPrimary: measurements.some((measurement) => measurement.primary),
            selectedMeasurementId: selected ? selected.measurement_id : null,
            executed: selected ? selected.executed : null,
            total: selected ? selected.total : null,
            pending: selected ? selected.pending : null,
            unit: selected && selected.unit ? selected.unit : '',
            rate4w: selected ? selected.rate_4w : null,
            plan6w: selected ? selected.plan_6w : null,
            endDate: activity.current_end_date,
            weeksToEnd: weeks.remaining,
            float: activity.float,
            critical: activity.critical_path,
            grossDelay: selected ? selected.gross_delay : null,
            projectDelay: selected ? selected.project_delay : null,
            sortDelay: activity.project_delay,
            staff: {
              budgeted: activity.staff ? activity.staff.budgeted : null,
              planned: activity.staff ? activity.staff.planned : null,
              actual: activity.staff ? activity.staff.actual : null,
            },
            sheetData: activity.sheet || {},
            weeklyProgress: activity.weekly_progress || [],
            monthlyHistory: activity.monthly_history || [],
            priority: review.priority || null,
            cause: review.cause || '',
            action: review.action || '',
            actionManager: review.action_manager || '',
            actionDate: review.action_date || null,
            actionStatus: review.action_status || null,
          }
          mapped.status = this.situationOf(mapped)
          return mapped
        })
        .sort((a, b) => {
          if (a.sortDelay === null && b.sortDelay === null) return 0
          if (a.sortDelay === null) return 1
          if (b.sortDelay === null) return -1
          return b.sortDelay - a.sortDelay
        })
    },

    selectedActivity() {
      if (!this.selectedActivityCode) return null
      return this.attentionActivities.find(activity => activity.code === this.selectedActivityCode) || null
    },

    // Jornada del proyecto (h/día), como en la planificación semanal
    projectManHour() {
      const configs = this.project && this.project.configs
      const value = configs && configs.man_hour != null && configs.man_hour !== '' ? Number(configs.man_hour) : null
      return value && value > 0 ? value : DEFAULT_MAN_HOUR
    },

    // Horas por persona y semana: jornada × días laborables de configs.work_days
    hoursPerWeek() {
      const workDays = resolveWorkDays(this.project && this.project.configs)
      const days = Object.keys(workDays).filter(day => workDays[day]).length || 5
      return this.projectManHour * days
    },

    /**
     * Ficha de la actividad elegida con la medición elegida.
     * A pasado: avance a origen, personas (planificadas = asignadas a la actividad) y h/ud plan / real.
     * A futuro:
     *  - ritmo necesario = pendiente / semanas hasta el fin;
     *  - personas necesarias = pendiente × h/ud real de las últimas 4 semanas / (días laborables que quedan × jornada);
     *  - h/ud necesario = HH programadas que quedan / pendiente. HH que quedan = personas planificadas × días que quedan × jornada,
     *    o HH programadas − HH registradas si la actividad no tiene personas.
     */
    sheet() {
      const a = this.selectedActivity
      if (!a) return null
      const m = this.measurementOf(a)
      const s = a.sheetData || {}
      const manHour = s.man_hour || this.projectManHour
      const pending = m ? m.pending : null
      const remainingDays = s.remaining_days || 0
      const plannedPeople = a.staff.planned
      const yield4w = m ? m.yield4w : null

      let remainingHours = null
      if (plannedPeople > 0 && remainingDays > 0) {
        remainingHours = plannedPeople * remainingDays * manHour
      } else if (s.scheduled_man_hours != null) {
        remainingHours = Math.max(s.scheduled_man_hours - (s.registered_man_hours || 0), 0)
      }

      return {
        unit: m ? m.unit : '',
        pending,
        past: {
          progress: { budgeted: a.baseline, planned: a.lastPlan, actual: a.actual },
          staff: { budgeted: null, planned: plannedPeople, actual: a.staff.actual },
          yield: { budgeted: null, planned: m ? m.yieldPlanned : null, actual: m ? m.yieldActual : null },
        },
        future: {
          rate: {
            remaining: null,
            plan6w: a.plan6w,
            needed: pending !== null && a.weeksToEnd > 0 ? pending / a.weeksToEnd : null,
            real4w: a.rate4w,
          },
          staff: {
            remaining: null,
            plan6w: remainingDays > 0 ? plannedPeople : null,
            needed: this.neededPeople(a, m),
            real4w: s.people_4w,
          },
          yield: {
            remaining: null,
            plan6w: m ? m.yieldPlanned : null,
            needed: remainingHours > 0 && pending > 0 ? remainingHours / pending : null,
            real4w: yield4w,
          },
        },
      }
    },

    sheetKpis() {
      const a = this.selectedActivity
      const d = this.sheet
      if (!a || !d) return []
      return [
        {
          label: this.$t('real_progress'),
          info: 'sheet_actual_progress',
          value: this.fmtPercent(a.actual, 0),
          hint: this.$t('performance_dashboard_kpi_actual_progress_hint', { baseline: this.fmtPercent(a.baseline, 0), plan: this.fmtPercent(a.lastPlan, 0) }),
        },
        {
          label: this.$t('performance_dashboard_kpi_project_delay_days'),
          info: 'sheet_project_delay',
          value: this.fmt(a.projectDelay, 0),
          hint: this.$t('performance_dashboard_kpi_project_delay_days_hint', { milestone: this.fmt(a.grossDelay, 0), float: this.fmt(a.float, 0) }),
        },
        {
          label: this.$t('performance_dashboard_kpi_weeks_to_program_end'),
          info: 'sheet_weeks_to_end',
          value: this.fmt(a.weeksToEnd),
          negative: a.weeksToEnd < 0,
          hint: this.$t('performance_dashboard_kpi_weeks_to_program_end_hint', { date: this.fmtDate(a.endDate), pending: `${this.fmt(d.pending)} ${d.unit}`.trim() }),
        },
        {
          label: this.$t('performance_dashboard_kpi_actual_yield', { unit: d.unit }),
          info: 'sheet_actual_yield',
          value: this.fmt(d.past.yield.actual, 2),
          hint: this.$t('performance_dashboard_kpi_actual_yield_hint', { budgeted: this.fmt(d.past.yield.budgeted, 2), planned: this.fmt(d.past.yield.planned, 2) }),
        },
      ]
    },

    pastRows() {
      if (!this.sheet) return []
      const { progress, staff, yield: yieldData } = this.sheet.past
      return [
        {
          label: this.$t('performance_dashboard_row_progress_percent'),
          values: [this.fmtPercent(progress.budgeted, 0), this.fmtPercent(progress.planned, 0), this.fmtPercent(progress.actual, 0)],
          alertActual: progress.actual < progress.planned,
        },
        {
          label: this.$t('performance_dashboard_row_staff'),
          values: [this.fmt(staff.budgeted), staff.planned === null ? '—' : this.fmt(staff.planned) + '*', this.fmt(staff.actual)],
        },
        {
          label: this.$t('performance_dashboard_row_yield', { unit: this.sheet.unit }),
          values: [this.fmt(yieldData.budgeted, 2), this.fmt(yieldData.planned, 2), this.fmt(yieldData.actual, 2)],
        },
      ]
    },

    futureRows() {
      if (!this.sheet) return []
      const { rate, staff, yield: yieldData } = this.sheet.future
      const row = (values, decimals) => [values.remaining, values.plan6w, values.needed, values.real4w].map(v => this.fmt(v, decimals))
      return [
        { label: this.$t('performance_dashboard_row_progress_rate', { unit: this.sheet.unit }), values: row(rate, 2) },
        { label: this.$t('performance_dashboard_row_staff'), values: row(staff, 1) },
        { label: this.$t('performance_dashboard_row_yield', { unit: this.sheet.unit }), values: row(yieldData, 2) },
      ]
    },

    situationKey() {
      const status = this.selectedActivity && this.selectedActivity.status
      if (!status) return 'performance_dashboard_not_defined'
      return 'performance_dashboard_situation_' + status
    },

    // Últimas 6 semanas: avance acumulado, personas medias de la semana y h/ud de la medición elegida
    weeklyCharts() {
      const a = this.selectedActivity
      if (!a || !this.sheet) return []
      const m = this.measurementOf(a)
      const staffWeekly = a.sheetData.staff_weekly || []
      const yieldWeekly = m ? m.weeklyYield : []
      const weekly = a.weeklyProgress.map((point) => {
        const staff = staffWeekly.find(week => week.number === point.number) || {}
        const yieldWeek = yieldWeekly.find(week => week.number === point.number) || {}
        return {
          label: `S${point.number}`,
          staff: { budgeted: null, planned: this.valueOrNull(staff.planned), actual: this.valueOrNull(staff.actual) },
          yield: { budgeted: null, planned: this.valueOrNull(yieldWeek.planned), actual: this.valueOrNull(yieldWeek.actual) },
        }
      })
      const colors = [COLORS.budgeted, COLORS.planned, COLORS.actual]
      const build = key => this.lineSeries(weekly, [
        [this.$t('performance_dashboard_col_budgeted_short'), row => row[key].budgeted],
        [this.$t('performance_dashboard_col_planned_short'), row => row[key].planned],
        [this.$t('actual'), row => row[key].actual],
      ])
      const progress = this.weeklyProgressRows
      return [
        {
          title: this.$t('performance_dashboard_chart_weekly_progress'),
          data: this.lineSeries(progress, [
            [this.$t('performance_dashboard_col_budgeted_short'), row => row.budgeted],
            [this.$t('performance_dashboard_col_planned_short'), row => row.planned],
            [this.$t('actual'), row => row.actual],
          ]),
          colors,
          scale: { min: 0, max: 100, suffix: '%' },
          note: this.$t('performance_dashboard_chart_weekly_progress_note'),
          info: 'weekly_progress',
        },
        { title: this.$t('performance_dashboard_chart_weekly_staff'), data: build('staff'), colors, scale: {}, info: 'weekly_staff' },
        { title: this.$t('performance_dashboard_row_yield', { unit: this.sheet.unit }), data: build('yield'), colors, scale: {}, info: 'weekly_yield' },
      ]
    },

    // % acumulado desde el inicio de la actividad hasta el fin de cada una de las últimas 6 semanas
    weeklyProgressRows() {
      const activity = this.selectedActivity
      const series = activity && activity.weeklyProgress ? activity.weeklyProgress : []
      return series.map((point) => ({
        label: `S${point.number}`,
        budgeted: point.budgeted,
        planned: point.planned,
        actual: point.actual,
      }))
    },

    historyLibrary() {
      return {
        legend: { display: true, position: 'bottom', labels: { boxWidth: 10, fontSize: 10 } },
        scales: {
          xAxes: [{
            ticks: { fontSize: 9, maxRotation: 0, autoSkip: false },
            scaleLabel: { display: true, labelString: this.$t('performance_dashboard_col_work_month'), fontSize: 10 },
          }],
          yAxes: [{ ticks: { fontSize: 10 } }],
        },
      }
    },

    // Personas y h/ud necesarios: los valores constantes de «Necesario para llegar», solo en meses futuros
    historyCharts() {
      const a = this.selectedActivity
      if (!a || !this.sheet) return []
      const neededStaff = this.sheet.future.staff.needed
      const neededYield = this.sheet.future.yield.needed
      const id = a.selectedMeasurementId
      const rows = (a.monthlyHistory || []).map((month) => {
        const bag = month.by_measurement || {}
        const figures = id != null ? (bag[id] || bag[String(id)] || {}) : {}
        const staff = month.staff || {}
        return {
          number: month.number,
          progress: month.progress,
          staff: {
            budgeted: null,
            planned: this.valueOrNull(staff.planned),
            actual: this.valueOrNull(staff.actual),
            needed: month.future ? neededStaff : null,
          },
          yield: {
            budgeted: null,
            planned: this.valueOrNull(figures.yield_planned),
            actual: this.valueOrNull(figures.yield),
            needed: month.future ? neededYield : null,
          },
        }
      })
      const colors = [COLORS.budgeted, COLORS.planned, COLORS.actual, COLORS.remaining]
      const series = (key, neededLabel) => this.historySeries(rows, [
        [this.$t('performance_dashboard_col_budgeted_short'), row => row[key].budgeted, [6, 4]],
        [this.$t('performance_dashboard_col_planned_short'), row => row[key].planned, []],
        [this.$t('actual'), row => row[key].actual, []],
        [neededLabel, row => row[key].needed, [2, 3]],
      ])
      return [
        { title: this.$t('performance_dashboard_chart_cumulative_progress'), data: series('progress', this.$t('performance_dashboard_col_needed_short')), colors, info: 'history_progress' },
        { title: this.$t('performance_dashboard_chart_monthly_staff'), data: series('staff', this.$t('performance_dashboard_col_needed_people_short')), colors, info: 'history_staff' },
        { title: this.$t('performance_dashboard_row_yield', { unit: this.sheet.unit }), data: series('yield', this.$t('performance_dashboard_col_needed_short')), colors, info: 'history_yield' },
      ]
    },

    // Últimos 12 meses de obra. Cantidad y rendimiento de la medición elegida.
    monthlyEvolution() {
      const activity = this.selectedActivity
      if (!activity || !activity.monthlyHistory || !activity.monthlyHistory.length) return []
      const history = activity.monthlyHistory.length > 12
        ? activity.monthlyHistory.slice(activity.monthlyHistory.length - 12)
        : activity.monthlyHistory
      const measurementId = activity.selectedMeasurementId
      return history.map((month) => {
        const bag = month.by_measurement || {}
        const figures = measurementId != null ? (bag[measurementId] || bag[String(measurementId)] || {}) : {}
        return {
          number: month.number,
          quantity: {
            budgeted: figures.budgeted != null ? figures.budgeted : null,
            planned: figures.planned != null ? figures.planned : null,
            actual: figures.actual != null ? figures.actual : null,
          },
          cumulative: {
            budgeted: month.progress ? month.progress.budgeted : null,
            actual: month.progress ? month.progress.actual : null,
          },
          staff: month.staff || { budgeted: null, planned: null, actual: null },
          yield: {
            budgeted: null,
            planned: figures.yield_planned != null ? figures.yield_planned : null,
            actual: figures.yield != null ? figures.yield : null,
          },
          lostHours: null,
        }
      })
    },

    canEditReview() {
      return !!(this.project && this.project.can_admin === true)
    },

    causeDirty() {
      if (!this.selectedActivity) return false
      return (this.actionDraft.cause || '') !== (this.selectedActivity.cause || '')
    },

    priorityDirty() {
      if (!this.selectedActivity) return false
      return (this.actionDraft.priority || '') !== (this.selectedActivity.priority || '')
    },

    systemDetail() {
      const activity = this.selectedActivity
      if (!activity) return { budget: null, measurements: [] }
      return {
        budget: activity.cost > 0 ? activity.cost : null,
        measurements: activity.measurements.map((measurement, index) => {
          const pending = measurement.total == null ? null : Math.max(measurement.total - (measurement.executed || 0), 0)
          const progress = measurement.total ? Math.round((measurement.executed / measurement.total) * 1000) / 10 : null
          return {
            id: measurement.id,
            code: measurement.code || `${activity.code}-M${index + 1}`,
            name: measurement.name,
            unit: measurement.unit || '',
            total: measurement.total,
            executed: measurement.executed,
            pending,
            origin: measurement.fromBoq ? 'budget' : 'manual',
            progress,
            rate4w: measurement.rate4w,
            plan6w: measurement.plan6w,
            weeksToFinish: measurement.rate4w > 0 && pending != null ? Math.round((pending / measurement.rate4w) * 10) / 10 : null,
            yieldActual: measurement.yieldActual,
            yield4w: measurement.yield4w,
            plannedHours: measurement.plannedHours,
            yieldPlanned: measurement.yieldPlanned,
            undated: measurement.undated || 0,
            primary: measurement.primary,
            parts: measurement.parts,
            series: measurement.series,
          }
        }),
      }
    },

    // La medición elegida en la tabla; sin elección, la primera de la actividad
    systemMeasurement() {
      const measurements = this.systemDetail.measurements
      const empty = { id: null, pending: null, unit: '', total: null, rate4w: null, plan6w: null, yieldActual: null, yield4w: null, yieldPlanned: null, weeksToFinish: null, name: '', series: [] }
      if (!this.selectedActivity || !measurements.length) return empty
      const id = this.selectedActivity.selectedMeasurementId
      return measurements.find(measurement => measurement.id === id) || measurements[0]
    },

    systemKpis() {
      const a = this.selectedActivity
      const m = this.systemMeasurement
      if (!a) return []
      const peopleNeeded = this.neededPeople(a, m)
      const days = value => (value === null || value === undefined ? '—' : this.$t('performance_dashboard_days', { count: value }))
      return [
        {
          label: this.$t('real_progress'),
          value: this.fmtPercent(a.actual, 1),
          mark: a.progressFromField,
          hint: this.$t('performance_dashboard_system_kpi_actual_progress_hint'),
        },
        {
          label: this.$t('performance_dashboard_system_kpi_last_plan_baseline'),
          value: `${this.fmtPercent(a.lastPlan)} / ${this.fmtPercent(a.baseline)}`,
          hint: this.$t('performance_dashboard_system_kpi_last_plan_baseline_hint'),
        },
        {
          label: this.$t('performance_dashboard_system_kpi_variance'),
          value: this.fmt(this.varianceOf(a)),
          negative: this.varianceOf(a) < 0,
          hint: this.$t('performance_dashboard_system_kpi_variance_hint'),
        },
        {
          label: this.$t('pending'),
          value: `${this.fmt(m.pending)} ${m.unit}`,
          hint: this.$t('performance_dashboard_system_kpi_pending_hint', { total: this.fmt(m.total), unit: m.unit }),
        },
        {
          label: this.$t('performance_dashboard_system_col_weeks_to_finish'),
          value: this.fmt(m.weeksToFinish),
          hint: this.$t('performance_dashboard_system_kpi_weeks_to_finish_hint'),
        },
        {
          label: this.$t('performance_dashboard_system_kpi_total_float'),
          value: days(a.float),
          hint: this.$t('performance_dashboard_system_kpi_total_float_hint'),
        },
        {
          label: this.$t('performance_dashboard_system_kpi_project_delay'),
          value: days(a.projectDelay),
          mark: a.projectDelay === null,
          hint: this.$t('performance_dashboard_system_kpi_project_delay_hint'),
        },
        {
          label: this.$t('people_needed'),
          value: this.fmt(peopleNeeded),
          hint: this.$t('performance_dashboard_system_kpi_people_needed_hint'),
        },
      ]
    },

    // 12 semanas hasta la del corte y 6 después (o los mismos datos agrupados por mes)
    systemSeries() {
      const weeks = this.systemMeasurement.series || []
      if (!weeks.length) return []
      if (this.seriesMode === 'weekly') {
        return weeks.map(week => ({
          label: `S${week.number}`,
          plan: week.plan,
          actual: week.actual,
          registeredHours: week.registered_hours,
          derivedHours: week.derived_hours,
        }))
      }

      const sum = values => (values.every(value => value === null || value === undefined)
        ? null
        : Math.round(values.reduce((total, value) => total + (value || 0), 0) * 100) / 100)
      const months = []
      weeks.forEach((week) => {
        const label = moment(week.start).format('MM/YY')
        let month = months.find(item => item.label === label)
        if (!month) {
          month = { label, weeks: [] }
          months.push(month)
        }
        month.weeks.push(week)
      })
      return months.map(month => ({
        label: month.label,
        plan: sum(month.weeks.map(week => week.plan)),
        actual: sum(month.weeks.map(week => week.actual)),
        registeredHours: sum(month.weeks.map(week => week.registered_hours)),
        derivedHours: sum(month.weeks.map(week => week.derived_hours)),
      }))
    },

    seriesRows() {
      const unit = this.systemMeasurement.unit
      return [
        { key: 'plan', label: this.$t('performance_dashboard_system_row_plan', { unit }) },
        { key: 'actual', label: this.$t('performance_dashboard_system_row_actual', { unit }) },
        { key: 'registeredHours', label: this.$t('performance_dashboard_system_row_registered_hours') },
        { key: 'derivedHours', label: this.$t('performance_dashboard_system_row_derived_hours') },
      ]
    },

    seriesChartData() {
      return this.lineSeries(this.systemSeries, this.seriesRows.slice(0, 2).map(row => [row.label, period => period[row.key]]))
    },

    // Cambia al elegir otra actividad o medición → la simulación vuelve a los valores del sistema
    simulationSource() {
      return this.selectedActivity ? `${this.selectedActivity.code}|${this.systemMeasurement.id}` : null
    },

    simulationFields() {
      const unit = this.systemMeasurement.unit
      return [
        { key: 'pending', step: '0.01', label: this.$t('performance_dashboard_sim_pending', { unit }) },
        { key: 'nextRate', step: '0.01', label: this.$t('performance_dashboard_sim_next_rate', { unit }) },
        { key: 'planRate', step: '0.01', label: this.$t('performance_dashboard_sim_plan_rate', { unit }) },
        { key: 'nextYield', step: '0.01', label: this.$t('performance_dashboard_sim_next_yield', { unit }) },
        { key: 'float', step: '1', label: this.$t('performance_dashboard_sim_float') },
      ]
    },

    simulationResults() {
      const s = this.simulation
      const unit = this.systemMeasurement.unit
      const pending = toNumber(s.pending)
      const nextRate = toNumber(s.nextRate)
      const planRate = toNumber(s.planRate)
      const nextYield = toNumber(s.nextYield)
      const float = toNumber(s.float) || 0

      const weeksToEnd = s.endDate ? moment(s.endDate).diff(moment().startOf('day'), 'days') / 7 : null
      const weeksActual = pending !== null && nextRate > 0 ? pending / nextRate : null
      const weeksPlan = pending !== null && planRate > 0 ? pending / planRate : null
      const grossDelay = weeksActual !== null && weeksToEnd !== null ? Math.max(0, Math.round((weeksActual - weeksToEnd) * 7)) : null
      const projectDelay = grossDelay !== null ? Math.max(0, grossDelay - float) : null
      const neededRate = pending !== null && weeksToEnd > 0 ? pending / weeksToEnd : null
      const peopleNeeded = neededRate !== null && nextYield > 0 ? (neededRate * nextYield) / this.hoursPerWeek : null

      const noRate = this.$t('performance_dashboard_sim_reason_no_rate')
      const endPassed = this.$t(weeksToEnd === null ? 'performance_dashboard_sim_no_data' : 'performance_dashboard_sim_reason_end_passed')
      return [
        { label: this.$t('performance_dashboard_sim_weeks_to_end'), value: weeksToEnd, decimals: 1, negative: weeksToEnd < 0, reason: this.$t('performance_dashboard_sim_no_data') },
        { label: this.$t('performance_dashboard_sim_weeks_to_finish_actual'), value: weeksActual, decimals: 1, reason: noRate },
        { label: this.$t('performance_dashboard_sim_weeks_to_finish_plan'), value: weeksPlan, decimals: 1, reason: this.$t('performance_dashboard_sim_reason_no_plan_rate') },
        { label: this.$t('performance_dashboard_sim_gross_delay'), value: grossDelay, decimals: 0, reason: noRate },
        { label: this.$t('performance_dashboard_kpi_project_delay_days'), value: projectDelay, decimals: 0, reason: noRate },
        { label: this.$t('performance_dashboard_sim_needed_rate', { unit }), value: neededRate, decimals: 2, reason: endPassed },
        {
          label: this.$t('people_needed'),
          value: peopleNeeded,
          decimals: 1,
          reason: neededRate === null ? endPassed : this.$t('performance_dashboard_sim_reason_no_yield'),
        },
      ]
    },
  },

  watch: {
    simulationSource: {
      immediate: true,
      handler(source) {
        if (source) this.resetSimulation()
      },
    },

    selectedActivityCode() {
      this.resetActionDraft()
    },
  },

  mounted() {
    if (this.project == null) {
      this.$router.push('/projects')
      return
    }

    this.$store.dispatch('app/updatePageInfo', {
      info: {
        title: this.project.name,
        subtitle: this.$t('performance_dashboard'),
        menuOption: '2-1',
        hidePageTitle: true,
      },
    })

    this.loadPerformanceActivities()
  },

  methods: {
    async loadPerformanceActivities() {
      if (!this.projectWeek) return

      this.loadingPerformance = true
      try {
        const { data } = await axios.get(`/api/v2/project/${this.project.id}/dashboard-performance/activities`, {
          params: {
            week_start: this.projectWeek.start.format('YYYY-MM-DD'),
            week_end: this.projectWeek.end.format('YYYY-MM-DD'),
          },
        })
        const payload = data && data.success ? data.data : null
        this.performanceActivities = payload ? payload.activities : []
        this.attentionMeasurementIds = this.defaultAttentionMeasurements(this.performanceActivities)
        if (payload && payload.windows) {
          this.performanceWindows = payload.windows
        }
        if (payload && payload.summary) {
          this.performanceSummary = Object.assign({}, this.performanceSummary, payload.summary)
        }
        if (payload && payload.thresholds) {
          this.performanceThresholds = payload.thresholds
        }
        const first = this.attentionActivities[0]
        this.selectedActivityCode = first ? first.code : null
      } catch (error) {
        this.performanceActivities = []
        this.attentionMeasurementIds = {}
        this.selectedActivityCode = null
        this.$notify.error('error_loading_activities')
      } finally {
        this.loadingPerformance = false
      }
    },

    performanceSeriesOf(getValue) {
      return this.performanceActivities.map(activity => [activity.code || activity.name, getValue(activity)])
    },

    // La primaria queda elegida; si no hay primaria el select empieza vacío.
    defaultAttentionMeasurements(activities) {
      const selected = {}
      activities.forEach((activity) => {
        const measurements = activity.measurements || []
        const primary = measurements.find((measurement) => measurement.primary)
        if (primary) {
          selected[activity.id] = primary.measurement_id
        }
      })
      return selected
    },

    setAttentionMeasurement(activityId, measurementId) {
      const next = { ...this.attentionMeasurementIds }
      if (measurementId === '' || measurementId == null) {
        delete next[activityId]
      } else {
        next[activityId] = Number(measurementId)
      }
      this.attentionMeasurementIds = next
    },

    // Serie de chartkick; devuelve [] si no hay ningún valor para mostrar el mensaje de "sin datos"
    lineSeries(rows, series, labelOf = row => row.label) {
      const data = series.map(([name, getValue]) => ({ name, data: rows.map(row => [labelOf(row), getValue(row)]) }))
      const hasValues = data.some(s => s.data.some(([, value]) => value !== null && value !== undefined))
      return hasValues ? data : []
    },

    // Líneas del histórico mensual. El cuarto valor de cada serie es el trazo: continuo, raya o punto.
    historySeries(rows, series) {
      const data = series.map(([name, getValue, dash]) => ({
        name,
        data: rows.map(row => [String(row.number), getValue(row)]),
        dataset: { fill: false, lineTension: 0, pointRadius: 3, borderDash: dash, spanGaps: false },
      }))
      const hasValues = data.some(seriesItem => seriesItem.data.some(([, value]) => value !== null && value !== undefined))
      return hasValues ? data : []
    },

    measurementOf(activity) {
      if (!activity || !activity.measurements.length) return null
      return activity.measurements.find(measurement => measurement.id === activity.selectedMeasurementId) || null
    },

    // Personas necesarias = pendiente × h/ud real de las últimas 4 semanas / (días laborables que quedan × jornada)
    neededPeople(activity, measurement) {
      if (!activity || !measurement) return null
      const sheet = activity.sheetData || {}
      const days = sheet.remaining_days || 0
      const manHour = sheet.man_hour || this.projectManHour
      if (!(measurement.pending > 0) || !(measurement.yield4w > 0) || days <= 0) return null
      return (measurement.pending * measurement.yield4w) / (days * manHour)
    },

    varianceOf(activity) {
      if (activity.actual == null || activity.lastPlan == null) return null
      return activity.actual - activity.lastPlan
    },

    valueOrNull(value) {
      return value === undefined ? null : value
    },

    selectActivity(code) {
      this.selectedActivityCode = code
      this.$nextTick(() => {
        if (this.$refs.activitySheet) this.$refs.activitySheet.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },

    statusLabel(activity) {
      if (activity && activity.status && STATUSES[activity.status]) {
        return this.$t(STATUSES[activity.status].label)
      }
      return this.$t('performance_dashboard_not_defined')
    },

    situationOf(activity) {
      const hasMeasurement = activity.selectedMeasurementId != null
      const pending = hasMeasurement ? activity.pending : null
      const endPassed = activity.weeksToEnd != null && activity.weeksToEnd < 0
      const noRate = hasMeasurement && (activity.rate4w === null || activity.rate4w === undefined)
      if (activity.actual != null && activity.actual >= 100) {
        if (!hasMeasurement && activity.measurements.length) {
          return endPassed ? 'overdue_no_deadline' : 'in_progress'
        }
        return pending > 0 ? 'completed_with_pending' : 'completed'
      }
      if (endPassed && (pending === null || pending > 0)) return 'overdue_no_deadline'
      if (noRate && (pending === null || pending > 0)) return 'in_progress_no_rate'
      return 'in_progress'
    },

    reviewFrom(activity) {
      if (!activity) return emptyReview()
      return {
        priority: activity.priority || '',
        cause: activity.cause || '',
        action: activity.action || '',
        manager: activity.actionManager || '',
        date: activity.actionDate || null,
        status: activity.actionStatus || 'pending',
      }
    },

    resetActionDraft() {
      this.actionDraft = this.reviewFrom(this.selectedActivity)
    },

    async saveReview() {
      if (!this.canEditReview || !this.selectedActivity || this.savingReview) return
      const draft = this.actionDraft
      const activityId = this.selectedActivity.id
      this.savingReview = true
      try {
        const { data } = await axios.put(
          `/api/v2/project/${this.project.id}/dashboard-performance/activities/${activityId}/review`,
          {
            priority: draft.priority || null,
            cause: draft.cause || null,
            action: draft.action || null,
            action_manager: draft.manager || null,
            action_date: draft.date || null,
            action_status: draft.action ? (draft.status || 'pending') : null,
          }
        )
        if (!data || !data.success) {
          this.$notify.error('save_error')
          return
        }
        this.performanceActivities = this.performanceActivities.map((activity) => {
          if (activity.id !== activityId) return activity
          return Object.assign({}, activity, { review: data.review || null })
        })
        this.resetActionDraft()
        this.$notify.success('performance_dashboard_action_saved')
      } catch (error) {
        this.$notify.error('save_error')
      } finally {
        this.savingReview = false
      }
    },

    openInfo(topic) {
      this.infoTopic = topic
    },

    selectSystemMeasurement(id) {
      const activity = this.selectedActivity
      if (!activity || !activity.measurements.some(measurement => measurement.id === id)) return
      this.setAttentionMeasurement(activity.id, id)
    },

    resetSimulation() {
      const a = this.selectedActivity
      const m = this.systemMeasurement
      this.simulation = {
        pending: m.pending,
        nextRate: m.rate4w,
        planRate: m.plan6w,
        nextYield: m.yield4w != null ? m.yield4w : m.yieldActual,
        float: a.float,
        endDate: a.endDate,
      }
    },

    // '—' para valores sin dato
    fmt(value, decimals = 1) {
      if (value === null || value === undefined || Number.isNaN(value)) return '—'
      return new Intl.NumberFormat(this.$i18n.locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(value)
    },

    realClass(value) {
      return value != null ? 'font-bold text-red-dark' : ''
    },

    fmtPercent(value, decimals = 0) {
      const formatted = this.fmt(value, decimals)
      return formatted === '—' ? formatted : formatted + '%'
    },

    fmtSignedPercent(value, decimals = 1) {
      const formatted = this.fmtPercent(value, decimals)
      return value > 0 ? '+' + formatted : formatted
    },

    fmtDate(date) {
      return date ? moment(date).format('DD/MM/YYYY') : '—'
    },

    fmtCurrency(value, currency = 'EUR') {
      return new Intl.NumberFormat(this.$i18n.locale, {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(value || 0)
    },
  },
}
</script>
